/**
 * MultiSelectOption — the checkbox row of a multi-select pick list (ORCH-418)
 *
 * Purpose: Every multi-select Autocomplete renders its options through this, so each option
 *          shows a checkbox and the lists read the same everywhere (metadata list fields,
 *          RoleSelect, the workflow and AI agent forms). Pass MUI's `renderOption` props and
 *          its `selected` state; `indeterminate` is for the "All" row.
 */
import React from 'react';
import { Box, Checkbox } from '@mui/material';

export interface MultiSelectOptionProps extends React.HTMLAttributes<HTMLLIElement> {
  label: React.ReactNode;
  selected: boolean;
  indeterminate?: boolean;
  dataTestId?: string;
}

const MultiSelectOption: React.FC<MultiSelectOptionProps> = ({
  label,
  selected,
  indeterminate = false,
  dataTestId,
  ...liProps
}) => (
  <Box component="li" {...liProps} data-testid={dataTestId} sx={{ gap: 1 }}>
    <Checkbox
      checked={selected && !indeterminate}
      indeterminate={indeterminate}
      disableRipple
      tabIndex={-1}
      sx={{ p: 0.25 }}
      inputProps={{ 'aria-hidden': true }}
      data-testid="multi-select-option-checkbox"
    />
    {label}
  </Box>
);

/**
 * A ready-made `renderOption` for a multi-select Autocomplete: MUI's option props and
 * `selected` state, with the label `getLabel` gives
 */
export const renderMultiSelectOption =
  <T,>(getLabel: (option: T) => React.ReactNode) =>
  (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
    option: T,
    state: { selected: boolean }
  ) => {
    const { key, ...liProps } = props;
    return (
      <MultiSelectOption
        key={key}
        {...liProps}
        label={getLabel(option)}
        selected={state.selected}
      />
    );
  };

export default MultiSelectOption;
